/**
 * PARAMÈTRES → PÉRIPHÉRIQUES — le parc du magasin, TPE et imprimantes, déclaré depuis le siège.
 *
 * ⚠️ UN BLOC DE L'ONGLET, PLUS UN ÉCRAN (décision utilisateur 2026-10-03) : c'est un réglage du MAGASIN, il vit donc
 * dans Paramètres, sous les réglages d'impression ; son entrée de rail a disparu. 🔴 L'écran est À REVOIR
 * ENTIÈREMENT (RESTE-À-FAIRE, en tête) : il s'organise encore PAR CAISSE, quand le réglage est un fait du magasin.
 *
 * POURQUOI CET ÉCRAN EXISTE. Le produit PROMETTAIT cette fonction à neuf endroits — l'écran Magasin
 * du back-office et huit textes de la caisse, dont un toast au caissier — pendant que la table
 * `peripheral` était écrite par personne et lue par personne. Un opérateur qui corrigeait l'IP d'un
 * TPE croyait informer le siège ; personne au siège ne l'a jamais su (§23.0, décision utilisateur :
 * « on doit avoir les MÊMES paramètres entre BO et Tablette, et les échanges doivent être
 * bidirectionnels »).
 *
 * 🔴 ET CET ÉCRAN NE REFAIT PAS LA MÊME PROMESSE. La moitié TABLETTE n'existe pas encore : une caisse
 * ne lit pas ce parc et n'y écrit pas — elle tient toujours ses TPE dans des réglages `device_local`,
 * qui ne quittent pas l'appareil. Déclarer ici est donc UTILE (le parc se prépare depuis un poste qui
 * voit le réseau, et il descendra) mais **sans effet sur les caisses aujourd'hui**, et l'écran le DIT
 * en toutes lettres. C'est exactement ce que les neuf promesses ne faisaient pas.
 *
 * ⚠️ IL S'ORGANISE PAR CAISSE, ET C'EST UNE GARDE, PAS UNE MISE EN PAGE. Depuis que le rattachement
 * voyage, une erreur de saisie au siège ferait dialoguer la caisse 2 avec le terminal du comptoir 1
 * — à distance, et sans témoin sur place. Le risque que le catalogue des réglages nommait n'a pas
 * disparu, il a CHANGÉ DE MAIN. Une liste à plat où deux TPE se ressemblent le rendrait probable ;
 * un rattachement qui se lit « Caisse 01 → Ingenico au comptoir » le rend visible.
 *
 * ⚠️ LA CAISSE SE CHOISIT, ELLE NE SE TAPE PAS. Le sélecteur n'offre que les caisses DÉCLARÉES au
 * registre : aucune contrainte de base ne peut l'imposer (notre émetteur de DDL n'émet pas de clé
 * étrangère, SQLite ne les applique pas), donc la garde est là. Un champ libre aurait laissé écrire
 * « 1 » là où la machine s'annonce « 01 » — le dépôt a déjà payé cette confusion.
 *
 * ⚠️ LA CALIBRATION DU TIROIR SE LIT, ELLE NE SE RÈGLE PAS ICI (§19.1) : c'est une mesure physique
 * qu'on n'obtient qu'en ouvrant le tiroir, et personne au back-office ne peut en vérifier le
 * résultat. L'écran le DIT — un champ grisé sans explication se lit comme une panne.
 */
import { useCallback, useEffect, useState } from 'react';
import { EmptyState, Icon, Tooltip, showToast } from '@orgazone/ui-web';
import { api, type ParcPeripheriques, type PeripheriqueDuParc } from '../api';
import { codeCaisse } from '../caisse';
import { key, t, type Translatable } from '../i18n';

/** Les deux natures d'appareil — liste FERMÉE, celle que le descripteur accepte (`printer` | `tpe`). */
const NATURES: { readonly v: string; readonly l: Translatable }[] = [
  { v: 'tpe', l: key('Terminal de paiement') },
  { v: 'printer', l: key('Imprimante') },
];

/** Le nom d'une nature, ou sa valeur brute si elle vient d'ailleurs — on ne tait jamais l'inconnu. */
function nomNature(v: string): string {
  const connue = NATURES.find((n) => n.v === v);
  return connue ? t(connue.l) : v;
}

/** Ce que la calibration du tiroir veut dire, en clair. */
function nomCalibration(v: string | null): string | null {
  if (v === 'high') return t('broche haute = tiroir ouvert');
  if (v === 'low') return t('broche basse = tiroir ouvert');
  // ⚠️ Une valeur inconnue s'affiche telle quelle : la taire ferait croire qu'aucune mesure n'existe.
  return v;
}

/** Le parc du magasin, rendu dans l'onglet Périphériques de Paramètres. */
export function PeripheralFleet() {
  const [parc, setParc] = useState<ParcPeripheriques | null>(null);
  const [echec, setEchec] = useState<string | null>(null);

  const relire = useCallback(() => {
    api.storePeripherals()
      // ⚠️ L'ÉCHEC SE DIT : un parc vide se lirait « ce magasin n'a aucun appareil », et quelqu'un
      //    les redéclarerait par-dessus ceux qui existent — avec la même identité, donc la même ligne.
      .then((r) => { setParc(r); setEchec(null); })
      .catch((e) => { setParc(null); setEchec(e instanceof Error ? e.message : String(e)); });
  }, []);
  useEffect(relire, [relire]);

  return (
    <div>
      {/* 🔴 CE QUI N'EST PAS ENCORE BRANCHÉ SE DIT AVANT TOUT LE RESTE, et pas en note de bas de page :
          c'est précisément en TAISANT cet état que le produit a promis neuf fois une fonction absente. */}
      <div role="status" style={{ padding: '11px 14px', marginBottom: 14, border: '1px solid var(--amber)', background: 'var(--amber-bg)', borderRadius: 'var(--r-md)', fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.55 }}>
        {t('Les caisses ne lisent pas encore ce parc : elles gardent leurs terminaux dans leurs propres réglages. Ce que vous déclarez ici est enregistré et descendra — la moitié tablette arrive avec la prochaine livraison.')}
      </div>

      {echec ? (
        <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
          {t('Le parc n’a pas pu être lu : {failure}. Ce n’est pas « aucun appareil ».', { failure: echec })}
        </p>
      ) : parc === null ? (
        <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture du parc…')}</p>
      ) : (
        <ParcRendu parc={parc} reload={relire} />
      )}
    </div>
  );
}

/** Le parc RANGÉ PAR CAISSE — la forme de la réponse suit la forme de la question. */
function ParcRendu({ parc, reload }: { parc: ParcPeripheriques; reload: () => void }) {
  const [composeur, setComposeur] = useState(false);

  if (parc.registers.length === 0 && parc.rows.length === 0) {
    return (
      <EmptyState icon="tablet" title={t('Aucune caisse n’a encore synchronisé')}
        note={t('Le registre des caisses se crée à la première synchronisation — tant qu’il est vide, il n’y a aucune caisse à laquelle rattacher un appareil.')} />
    );
  }

  const libres = parc.rows.filter((p) => p.registerId === null);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => setComposeur(true)}
          style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: 'pointer' }}>
          {t('+ Déclarer un appareil')}
        </button>
      </div>

      {composeur ? (
        <Composeur registers={parc.registers} onFait={() => { setComposeur(false); reload(); }}
          onAnnule={() => setComposeur(false)} />
      ) : null}

      {parc.registers.map((r) => (
        <BlocCaisse key={r} registerId={r} appareils={parc.rows.filter((p) => p.registerId === r)} />
      ))}

      {/* ⚠️ LE PARC NON RATTACHÉ N'EST PAS UN RESTE : c'est ce qu'un outil de détection réseau vient
          de trouver, ou un appareil qu'on prépare. Le cacher le rendrait introuvable. */}
      <section style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <header style={{ padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <Icon name="layers" size={16} stroke={1.9} />
          <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Non rattachés')}</span>
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
            {t('appareils déclarés qui ne servent aucune caisse')}
          </span>
        </header>
        {libres.length === 0 ? (
          <p style={{ margin: 0, padding: '14px 16px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
            {t('Aucun — chaque appareil déclaré sert une caisse.')}
          </p>
        ) : libres.map((p) => (
          <LignePeripherique key={`${p.kind}:${p.name}`} p={p} registers={parc.registers} reload={reload} />
        ))}
      </section>
    </div>
  );
}

/** Une caisse et ses appareils — c'est ce bloc qui rend une erreur de rattachement VISIBLE. */
function BlocCaisse({ registerId, appareils }: { registerId: string; appareils: PeripheriqueDuParc[] }) {
  return (
    <section style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <header style={{ padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', display: 'flex', alignItems: 'center', gap: 9 }}>
        <Icon name="tablet" size={16} stroke={1.9} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>
          {codeCaisse(registerId)}
        </span>
      </header>
      {appareils.length === 0 ? (
        <p style={{ margin: 0, padding: '14px 16px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
          {/* ⚠️ Une caisse SANS appareil se dit : c'est un fait d'exploitation, pas un trou d'affichage. */}
          {t('Aucun appareil rattaché à cette caisse.')}
        </p>
      ) : appareils.map((p) => (
        <LignePeripherique key={`${p.kind}:${p.name}`} p={p} registers={[]} reload={() => undefined} />
      ))}
    </section>
  );
}

function LignePeripherique({ p, registers, reload }: {
  p: PeripheriqueDuParc; registers: string[]; reload: () => void;
}) {
  const [enCours, setEnCours] = useState(false);
  const calibration = nomCalibration(p.drawerOpenLevel);

  const rattacher = async (registerId: string) => {
    setEnCours(true);
    try {
      // ⚠️ L'IDENTITÉ EST COMPOSITE (kind, name) : c'est elle que le producteur compare, et c'est
      //    pourquoi on la renvoie ENTIÈRE — un rattachement n'est pas une création déguisée.
      await api.createReferentielEntry('peripheral', { kind: p.kind, name: p.name, registerId },
        `periph:attach:${p.kind}:${p.name}:${registerId}`);
      showToast(t('« {v} » rattaché à la caisse {c}.', { v: p.name, c: codeCaisse(registerId) }));
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px minmax(140px,1fr) minmax(0,150px) minmax(0,160px) minmax(0,170px)', gap: 12, padding: '11px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
      <span style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 700, whiteSpace: 'nowrap' }}>
        {nomNature(p.kind)}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
        {p.isDefault ? (
          <span style={{ display: 'block', fontSize: 'var(--t-micro)', fontWeight: 700, color: 'var(--teal-800)', marginTop: 1 }}>{t('par défaut')}</span>
        ) : null}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: p.ip ? 'var(--ink-700)' : 'var(--ink-400)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {/* ⚠️ « adresse inconnue » et non un tiret : un appareil sans IP ne se joint pas, et ça se dit. */}
        {p.ip ? (p.port ? `${p.ip}:${p.port}` : p.ip) : t('adresse inconnue')}
      </span>
      <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', minWidth: 0 }}>
        {p.series ?? p.concertVersion ?? ''}
        {calibration ? (
          <Tooltip
            label={t('Calibration du tiroir')}
            content={t('Mesurée sur l’appareil en ouvrant le tiroir. Elle remonte ici pour que le support la lise sans appeler le magasin ; elle se règle en caisse, où le résultat se vérifie.')}>
            <span style={{ display: 'block', fontSize: 'var(--t-micro)', color: 'var(--ink-400)', marginTop: 1 }}>{calibration}</span>
          </Tooltip>
        ) : null}
      </span>
      <span>
        {/* Le rattachement ne s'offre que sur un appareil LIBRE : la liste des caisses n'est passée
            que là. Ailleurs, la ligne vit déjà sous le bloc de SA caisse — la déplacer se fera quand
            la moitié tablette existera et qu'un débranchement aura un sens à l'écran. */}
        {registers.length > 0 ? (
          <select value="" disabled={enCours}
            onChange={(e) => { if (e.target.value) void rattacher(e.target.value); }}
            aria-label={t('Rattacher {label} à une caisse', { label: p.name })}
            style={{ width: '100%', height: 30, padding: '0 8px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', boxSizing: 'border-box' }}>
            <option value="">{t('Rattacher à…')}</option>
            {registers.map((r) => <option key={r} value={r}>{codeCaisse(r)}</option>)}
          </select>
        ) : null}
      </span>
    </div>
  );
}

/** DÉCLARER un appareil — ce qu'un poste qui voit le réseau peut poser pour toute la boutique. */
function Composeur({ registers, onFait, onAnnule }: {
  registers: string[]; onFait: () => void; onAnnule: () => void;
}) {
  const [kind, setKind] = useState('tpe');
  const [nom, setNom] = useState('');
  const [ip, setIp] = useState('');
  const [port, setPort] = useState('');
  const [caisse, setCaisse] = useState('');
  const [enCours, setEnCours] = useState(false);
  const pret = !!nom.trim();

  const creer = async () => {
    setEnCours(true);
    try {
      await api.createReferentielEntry('peripheral', {
        kind, name: nom.trim(),
        // ⚠️ `null` ET NON LA CHAÎNE VIDE : le producteur n'écrit que ce qui est FOURNI, et une chaîne
        //    vide poserait une adresse « rien » là où l'absence veut dire « pas encore connue ».
        ip: ip.trim() || null,
        port: port.trim() || null,
        registerId: caisse || null,
      }, `periph:create:${kind}:${nom.trim()}`);
      showToast(t('« {v} » déclaré au parc.', { v: nom.trim() }));
      onFait();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  return (
    <div style={{ border: '1px solid var(--teal-300)', background: 'var(--teal-100)', borderRadius: 'var(--r-md)', padding: '14px 16px' }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ width: 176 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Nature')}</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            style={{ width: '100%', height: 40, padding: '0 10px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', boxSizing: 'border-box' }}>
            {NATURES.map((n) => <option key={n.v} value={n.v}>{t(n.l)}</option>)}
          </select>
        </label>
        <label style={{ flex: 1, minWidth: 180 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Nom de l’appareil')}</span>
          <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder={t('Ingenico du comptoir, Epson réserve…')}
            style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
        </label>
        <label style={{ width: 160 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Adresse réseau')}</span>
          <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="192.168.1.42"
            style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
        </label>
        <label style={{ width: 96 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Port')}</span>
          <input value={port} onChange={(e) => setPort(e.target.value)} placeholder="8888"
            style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
        </label>
        <label style={{ width: 176 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Caisse')}</span>
          {/* ⚠️ UN SÉLECTEUR, JAMAIS UN CHAMP LIBRE : seules les caisses DÉCLARÉES sont offertes. */}
          <select value={caisse} onChange={(e) => setCaisse(e.target.value)}
            style={{ width: '100%', height: 40, padding: '0 10px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', boxSizing: 'border-box' }}>
            <option value="">{t('Aucune pour l’instant')}</option>
            {registers.map((r) => <option key={r} value={r}>{codeCaisse(r)}</option>)}
          </select>
        </label>
      </div>
      <p style={{ margin: '10px 0 0', fontSize: '12px', color: 'var(--teal-800)', lineHeight: 1.5 }}>
        {t('Le nom identifie l’appareil pour toute la boutique — deux appareils de même nature ne peuvent pas le partager.')}
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" disabled={!pret || enCours} onClick={() => void creer()}
          style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: pret ? 'var(--teal-800)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: pret ? 'pointer' : 'default' }}>
          {t('Déclarer')}
        </button>
        <button type="button" onClick={onAnnule}
          style={{ height: 38, padding: '0 13px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}>
          {t('Annuler')}
        </button>
      </div>
    </div>
  );
}
